import {RemoteConstantsModel} from "../models/remoteConstants.model";
import {diffRecord, recordsEqual} from "./recordDiff";
import {MapSectionKey, RecordDiff, SectionDiff, SectionEntry} from "./branchDiff";

// Builders for the two non-record section flavors used by the Compare Branches
// tool: key→value MAP sections (localization, sounds, images, imagePacks,
// constructionImagePacks) and
// the singleton GLOBALS section (the model's top-level scalars + config
// objects). Both reuse the field-level diff engine in recordDiff.ts; only the
// shape of the section and the migration commands differ.

type NameResolver = (key: string) => string;

// ---------------------------------------------------------------------------
// Map sections — one entry per key. Values are treated as opaque units: a
// differing key surfaces a single whole-value change (current → target) and is
// migrated/removed wholesale. This is the right granularity for image/sound
// metadata objects and image-pack matrices; for localization (string values)
// it reads as a plain text change.
// ---------------------------------------------------------------------------

interface MapEntrySource {
    // The location of the value within the section's slice of the model.
    // Single-level maps (sounds/images/imagePacks): [key]. Localization is
    // two-level: [lang, key].
    path: string[];
    // Stable identity for the row — the joined path, safe because map keys
    // don't contain NULs.
    identity: string;
    // Display name for the row.
    name: string;
    value: unknown;
}

function flattenSingleLevel(map: Record<string, unknown> | undefined): MapEntrySource[] {
    const out: MapEntrySource[] = [];
    for (const key of Object.keys(map ?? {})) {
        out.push({path: [key], identity: key, name: key, value: (map as Record<string, unknown>)[key]});
    }
    return out;
}

function flattenLocalization(
    loc: { [lang: string]: { [key: string]: string } } | undefined,
): MapEntrySource[] {
    const out: MapEntrySource[] = [];
    for (const lang of Object.keys(loc ?? {})) {
        const table = (loc as Record<string, Record<string, string>>)[lang] ?? {};
        for (const key of Object.keys(table)) {
            out.push({
                path: [lang, key],
                identity: `${lang} ${key}`,
                name: `${key}  [${lang}]`,
                value: table[key],
            });
        }
    }
    return out;
}

function buildMapSection(
    title: string,
    sectionKey: MapSectionKey,
    current: MapEntrySource[],
    target: MapEntrySource[],
): SectionDiff {
    const currByIdentity = new Map(current.map(e => [e.identity, e]));
    const targByIdentity = new Map(target.map(e => [e.identity, e]));

    const onlyInCurrent: SectionEntry[] = [];
    const onlyInTarget: SectionEntry[] = [];
    const differs: RecordDiff[] = [];

    const allIdentities = new Set<string>();
    currByIdentity.forEach((_v, k) => allIdentities.add(k));
    targByIdentity.forEach((_v, k) => allIdentities.add(k));

    allIdentities.forEach(identity => {
        const c = currByIdentity.get(identity);
        const t = targByIdentity.get(identity);
        if (c && t) {
            if (recordsEqual(c.value, t.value)) return;
            differs.push({
                identity,
                name: c.name,
                path: c.path,
                fieldChanges: [{
                    path: c.path,
                    kind: 'scalar',
                    current: c.value,
                    target: t.value,
                }],
            });
        } else if (c) {
            onlyInCurrent.push({identity, name: c.name, path: c.path});
        } else if (t) {
            onlyInTarget.push({identity, name: t.name, path: t.path});
        }
    });

    const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
    onlyInCurrent.sort(byName);
    onlyInTarget.sort(byName);
    differs.sort(byName);

    return {
        title,
        sectionKey,
        flavor: 'map',
        onlyInCurrent,
        onlyInTarget,
        differs,
        identical: onlyInCurrent.length === 0 && onlyInTarget.length === 0 && differs.length === 0,
    };
}

export function diffLocalizationSection(
    current: RemoteConstantsModel,
    target: RemoteConstantsModel,
): SectionDiff {
    return buildMapSection(
        'Localization', 'localization',
        flattenLocalization(current.localization),
        flattenLocalization(target.localization),
    );
}

export function diffSoundsSection(current: RemoteConstantsModel, target: RemoteConstantsModel): SectionDiff {
    return buildMapSection('Sounds', 'sounds',
        flattenSingleLevel(current.sounds as Record<string, unknown>),
        flattenSingleLevel(target.sounds as Record<string, unknown>));
}

export function diffImagesSection(current: RemoteConstantsModel, target: RemoteConstantsModel): SectionDiff {
    return buildMapSection('Images', 'images',
        flattenSingleLevel(current.images as Record<string, unknown>),
        flattenSingleLevel(target.images as Record<string, unknown>));
}

export function diffImagePacksSection(current: RemoteConstantsModel, target: RemoteConstantsModel): SectionDiff {
    return buildMapSection('Image Packs', 'imagePacks',
        flattenSingleLevel(current.imagePacks as Record<string, unknown>),
        flattenSingleLevel(target.imagePacks as Record<string, unknown>));
}

export function diffConstructionImagePacksSection(current: RemoteConstantsModel, target: RemoteConstantsModel): SectionDiff {
    return buildMapSection('Construction Images', 'constructionImagePacks',
        flattenSingleLevel(current.constructionImagePacks as Record<string, unknown>),
        flattenSingleLevel(target.constructionImagePacks as Record<string, unknown>));
}

// ---------------------------------------------------------------------------
// Globals section — the model's top-level scalars + nested config objects,
// diffed as one pseudo-record. Per-field resolve via ResolveGlobalFieldCommand.
// Excluded from the list: array sections (their own sections), the map sections
// above, `archive` (asset bookkeeping), and `frame`/`versionNumber` (machine-
// managed counters that must never be cross-branch synced).
// ---------------------------------------------------------------------------

export const GLOBAL_KEYS: (keyof RemoteConstantsModel)[] = [
    'startingUnit', 'startingBuilding',
    'startingGold', 'capitalCityGoldPerTurn', 'cityGoldPerTurn',
    'attackDamageFormula', 'minimumDamage',
    'retaliatoryDamageFormula', 'minimumRetaliatoryDamage',
    'criticalHealthPercent', 'criticalAttackPenalty', 'criticalDefensePenalty',
    'playerScoreFormula', 'elevationNoise', 'temperatureNoise',
    'techConfig', 'techTreeRender',
];

function pickGlobals(model: RemoteConstantsModel): Record<string, unknown> {
    const o: Record<string, unknown> = {};
    for (const k of GLOBAL_KEYS) {
        const v = model[k];
        if (v !== undefined) o[k as string] = v;
    }
    return o;
}

export function diffGlobalsSection(
    current: RemoteConstantsModel,
    target: RemoteConstantsModel,
    resolveName: NameResolver,
): SectionDiff {
    const c = pickGlobals(current);
    const t = pickGlobals(target);
    const fieldChanges = recordsEqual(c, t)
        ? []
        : diffRecord(c, t).map(fc => {
            // startingUnit/startingBuilding hold entity ids — show a
            // friendly name alongside the raw value where we can resolve it.
            const named = (v: unknown) => (typeof v === 'string' ? resolveName(v) : v);
            const top = fc.path[0];
            if ((top === 'startingUnit' || top === 'startingBuilding')
                && fc.kind === 'scalar') {
                return {...fc, displayCurrent: named(fc.current), displayTarget: named(fc.target)};
            }
            return fc;
        });

    const differs: RecordDiff[] = fieldChanges.length > 0
        ? [{identity: 'globals', name: 'Game Settings', fieldChanges}]
        : [];

    return {
        title: 'Game Settings',
        sectionKey: 'globals',
        flavor: 'globals',
        onlyInCurrent: [],
        onlyInTarget: [],
        differs,
        identical: differs.length === 0,
    };
}
